import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  BrainCircuit,
  CheckCircle2,
  Eye,
  EyeOff,
  Filter,
  Loader2,
  LockKeyhole,
  LogIn,
  Mail,
  Megaphone,
  MousePointerClick,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserPlus,
  Wand2,
  X,
} from 'lucide-react';
import {
  requestPasswordReset,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from '../services/supabase';
import { toast } from './Toaster';

const FIELD_STATE = {
  name: { focus: 'nameFocus', typing: 'nameTyping', card: 'audience' },
  email: { focus: 'emailFocus', typing: 'emailTyping', card: 'email' },
  company: { focus: 'companyFocus', typing: 'companyTyping', card: 'campaign' },
  password: { focus: 'passwordFocus', typing: 'passwordFocus', card: 'security' },
};

const INITIAL_FORM = {
  name: '',
  email: '',
  company: '',
  password: '',
  acceptedTerms: false,
};

function getCardForState(sceneState, validationField) {
  if (sceneState === 'validationError') return FIELD_STATE[validationField]?.card || '';
  if (sceneState === 'nameFocus' || sceneState === 'nameTyping') return 'audience';
  if (sceneState === 'emailFocus' || sceneState === 'emailTyping') return 'email';
  if (sceneState === 'companyFocus' || sceneState === 'companyTyping') return 'campaign';
  if (sceneState === 'passwordFocus' || sceneState === 'passwordVisible') return 'security';
  if (sceneState === 'submitting') return 'all';
  if (sceneState === 'success') return 'success';
  if (sceneState === 'error') return 'error';
  return '';
}

function validateForm(values, mode) {
  const errors = {};

  if (mode === 'register') {
    if (!values.name.trim()) errors.name = 'Enter your full name.';
    if (!values.company.trim()) errors.company = 'Enter your company name.';
    if (!values.acceptedTerms) errors.acceptedTerms = 'Accept the Terms and acknowledge the Privacy Policy to continue.';
  }

  if (!values.email.trim()) {
    errors.email = 'Enter your work email.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Enter a valid work email.';
  }

  if (mode !== 'forgot' && !values.password) {
    errors.password = 'Enter a password.';
  } else if (mode !== 'forgot' && values.password.length < 6) {
    errors.password = 'Use at least 6 characters.';
  }

  return errors;
}

function AuthModal({ open, onClose, onAuth, onOpenLegal }) {
  if (!open) return null;

  return (
    <div className="auth-overlay auth-page-overlay" onClick={onClose}>
      <SignupPage onClose={onClose} onAuth={onAuth} onOpenLegal={onOpenLegal} />
    </div>
  );
}

function SignupPage({ onClose, onAuth, onOpenLegal }) {
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState(INITIAL_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [sceneState, setSceneState] = useState('idle');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [validationField, setValidationField] = useState('');
  const typingTimers = useRef({});
  const errorResetTimer = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => () => {
    Object.values(typingTimers.current).forEach(clearTimeout);
    clearTimeout(errorResetTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const relevantCard = getCardForState(sceneState, validationField);
  const isSignup = mode === 'register';
  const isForgot = mode === 'forgot';

  const resetForMode = (nextMode) => {
    setMode(nextMode);
    setForm(INITIAL_FORM);
    setErrors({});
    setApiError('');
    setValidationField('');
    setFocusedField('');
    setShowPassword(false);
    setSceneState('idle');
    setAnnouncement(nextMode === 'register' ? 'Signup form selected.' : nextMode === 'forgot' ? 'Password reset form selected.' : 'Sign in form selected.');
  };

  const setFieldScene = (field, typing = false) => {
    const config = FIELD_STATE[field];
    if (!config || busy) return;

    setSceneState(typing ? config.typing : config.focus);

    if (typing) {
      clearTimeout(typingTimers.current[field]);
      typingTimers.current[field] = setTimeout(() => {
        setSceneState((current) => (current === config.typing ? config.focus : current));
      }, 420);
    }
  };

  const handleFocus = (field) => {
    setFocusedField(field);
    setApiError('');
    setValidationField('');
    setFieldScene(field, false);
  };

  const handleBlur = () => {
    setFocusedField('');
    if (!busy && sceneState !== 'success') {
      setTimeout(() => setSceneState((current) => (
        ['submitting', 'success', 'error', 'validationError'].includes(current) ? current : 'idle'
      )), 80);
    }
  };

  const update = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
    if (name !== 'password') setFieldScene(name, true);
  };

  const togglePassword = () => {
    setShowPassword((current) => {
      const next = !current;
      setSceneState(next ? 'passwordVisible' : 'passwordFocus');
      setAnnouncement(next ? 'Password is visible.' : 'Password is hidden.');
      return next;
    });
  };

  const triggerValidationError = (nextErrors) => {
    const firstField = Object.keys(nextErrors)[0];
    setErrors(nextErrors);
    setValidationField(firstField);
    setSceneState('validationError');
    setAnnouncement(nextErrors[firstField]);

    clearTimeout(errorResetTimer.current);
    errorResetTimer.current = setTimeout(() => {
      if (focusedField && FIELD_STATE[focusedField]) setSceneState(FIELD_STATE[focusedField].focus);
      else setSceneState('idle');
    }, 520);
  };

  async function submit(event) {
    event.preventDefault();
    const nextErrors = validateForm(form, mode);

    if (Object.keys(nextErrors).length) {
      triggerValidationError(nextErrors);
      return;
    }

    setBusy(true);
    setApiError('');
    setValidationField('');
    setSceneState('submitting');
    setAnnouncement(isSignup ? 'Creating your AI marketing workspace.' : isForgot ? 'Sending a password reset link.' : 'Signing in.');

    try {
      if (isForgot) {
        await requestPasswordReset(form.email.trim());
        const message = 'If an account exists for that email, a password reset link has been sent.';
        setSceneState('success');
        setAnnouncement(message);
        toast(message, 'success');
        return;
      }
      const payload = isSignup
        ? { email: form.email.trim(), password: form.password, name: form.name.trim(), company: form.company.trim() }
        : { email: form.email.trim(), password: form.password };
      const data = await (isSignup ? signUpWithEmail(payload) : signInWithEmail(payload));

      if (isSignup && !data.session) {
        const message = 'Check your email to confirm your account, then sign in.';
        setSceneState('success');
        setAnnouncement(message);
        toast(message, 'success');
        return;
      }

      setSceneState('success');
      setAnnouncement(isSignup ? 'Workspace ready.' : 'Welcome back.');
      toast(isSignup ? 'Workspace ready!' : `Welcome back, ${data.user.name || data.user.email}!`, 'success');

      closeTimer.current = setTimeout(() => {
        onAuth(data.user);
        onClose();
      }, 1350);
    } catch (err) {
      const message = err.message || (isSignup ? 'Could not create your account. Please try again.' : 'Could not sign in. Please try again.');
      setApiError(message);
      setSceneState('error');
      setAnnouncement(message);
      toast(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  const handleGoogle = async () => {
    if (isSignup && !form.acceptedTerms) {
      triggerValidationError({ acceptedTerms: 'Accept the Terms and acknowledge the Privacy Policy to continue.' });
      return;
    }
    setBusy(true);
    setApiError('');
    setSceneState('submitting');
    setAnnouncement('Opening Google sign in.');
    try {
      await signInWithGoogle();
    } catch (err) {
      const message = err.message || 'Could not start Google sign in.';
      setApiError(message);
      setSceneState('error');
      setAnnouncement(message);
      toast(message, 'error');
      setBusy(false);
    }
  };

  return (
    <div
      className={`auth-modal signup-page auth-state-${sceneState} ${showPassword ? 'auth-password-is-visible' : ''}`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-title"
    >
      <button className="auth-close signup-close" onClick={onClose} aria-label="Close signup dialog">
        <X size={18} />
      </button>

      <AnimatedMarketingScene
        state={sceneState}
        relevantCard={relevantCard}
        form={form}
        validationField={validationField}
        passwordVisible={showPassword}
        mode={mode}
      />

      <SignupForm
        mode={mode}
        form={form}
        errors={errors}
        apiError={apiError}
        busy={busy}
        showPassword={showPassword}
        announcement={announcement}
        onSubmit={submit}
        onChange={update}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onTogglePassword={togglePassword}
        onGoogle={handleGoogle}
        onModeChange={resetForMode}
        onOpenLegal={onOpenLegal}
      />
    </div>
  );
}

function SignupForm({
  mode,
  form,
  errors,
  apiError,
  busy,
  showPassword,
  announcement,
  onSubmit,
  onChange,
  onFocus,
  onBlur,
  onTogglePassword,
  onGoogle,
  onModeChange,
  onOpenLegal,
}) {
  const isSignup = mode === 'register';
  const isForgot = mode === 'forgot';

  return (
    <section className="auth-content signup-form-panel">
      <div className="signup-logo-row">
        <span className="signup-mini-logo" aria-hidden="true"><Sparkles size={17} /></span>
        <span>AWN AdPulse</span>
      </div>

      <p className="eyebrow">AI marketing workspace</p>
      <h2 id="signup-title" className="auth-title signup-title">
        {isSignup ? 'Start growing with AI' : isForgot ? 'Reset your password' : 'Welcome back to AI growth'}
      </h2>
      <p className="auth-subtitle signup-subtitle">
        {isSignup
          ? 'Create smarter campaigns in minutes.'
          : isForgot
            ? 'Enter your email and we will send a secure reset link.'
            : 'Sign in to keep building smarter campaigns.'}
      </p>

      <form className="auth-form signup-form" onSubmit={onSubmit} noValidate>
        {isSignup && (
          <InputField
            label="Full name"
            name="name"
            value={form.name}
            onChange={onChange}
            onFocus={() => onFocus('name')}
            onBlur={onBlur}
            placeholder="Your full name"
            autoComplete="name"
            error={errors.name}
          />
        )}

        <InputField
          label="Work email"
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          onFocus={() => onFocus('email')}
          onBlur={onBlur}
          placeholder="you@company.com"
          autoComplete="email"
          error={errors.email}
        />

        {isSignup && (
          <label className={`auth-legal-check ${errors.acceptedTerms ? 'has-error' : ''}`}>
            <input
              type="checkbox"
              name="acceptedTerms"
              checked={form.acceptedTerms}
              onChange={onChange}
            />
            <span>
              I agree to the <button type="button" className="text-link" onClick={() => onOpenLegal?.('terms')}>Terms and Conditions</button>
              {' '}and acknowledge the <button type="button" className="text-link" onClick={() => onOpenLegal?.('privacy')}>Privacy Policy</button>.
              {errors.acceptedTerms && <small>{errors.acceptedTerms}</small>}
            </span>
          </label>
        )}

        {isSignup && (
          <InputField
            label="Company name"
            name="company"
            value={form.company}
            onChange={onChange}
            onFocus={() => onFocus('company')}
            onBlur={onBlur}
            placeholder="Acme Growth Labs"
            autoComplete="organization"
            error={errors.company}
          />
        )}

        {!isForgot && <InputField
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={onChange}
          onFocus={() => onFocus('password')}
          onBlur={onBlur}
          placeholder="At least 6 characters"
          autoComplete={isSignup ? 'new-password' : 'current-password'}
          error={errors.password}
          trailingButton={(
            <button
              type="button"
              className="password-eye-button"
              onClick={onTogglePassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        />}

        {apiError && (
          <p className="signup-api-error" role="alert">
            <AlertTriangle size={15} /> {apiError}
          </p>
        )}

        <div className="auth-live-region" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>

        <button type="submit" className={`primary-button auth-submit signup-submit ${busy ? 'is-loading' : ''}`} disabled={busy}>
          {busy ? <Loader2 size={16} className="spin-icon" /> : isSignup ? <UserPlus size={16} /> : isForgot ? <Mail size={16} /> : <LogIn size={16} />}
          {busy
            ? (isSignup ? 'Creating workspace...' : isForgot ? 'Sending link...' : 'Signing in...')
            : isSignup ? 'Create account' : isForgot ? 'Send reset link' : 'Sign in'}
        </button>

        {!isForgot && <SocialSignupButton onClick={onGoogle} />}
      </form>

      {isSignup && <p className="signup-trust"><ShieldCheck size={14} /> No credit card required</p>}

      {!isForgot ? (
        <>
          <button type="button" className="auth-forgot-link" onClick={() => onModeChange('forgot')}>Forgot Password?</button>
          <p className="auth-switch signup-switch">
            {isSignup ? 'Already have an account?' : "Don't have an account?"}
            <button type="button" onClick={() => onModeChange(isSignup ? 'login' : 'register')}>
              {isSignup ? 'Sign in' : 'Create account'}
            </button>
          </p>
        </>
      ) : (
        <p className="auth-switch signup-switch">
          Remembered your password?
          <button type="button" onClick={() => onModeChange('login')}>Back to sign in</button>
        </p>
      )}
      <p className="auth-legal-links">
        <button type="button" onClick={() => onOpenLegal?.('privacy')}>Privacy</button>
        <button type="button" onClick={() => onOpenLegal?.('cookies')}>Cookies</button>
        <button type="button" onClick={() => onOpenLegal?.('terms')}>Terms</button>
      </p>
    </section>
  );
}

function InputField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  autoComplete,
  error,
  trailingButton,
}) {
  const errorId = `${name}-error`;

  return (
    <label className={`signup-input-field ${error ? 'invalid' : ''}`}>
      <span>{label}</span>
      <span className={`signup-input-shell ${trailingButton ? 'has-trailing' : ''}`}>
        <input
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          required
          minLength={name === 'password' ? 6 : undefined}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
        />
        {trailingButton}
      </span>
      {error && <span className="signup-field-error" id={errorId}>{error}</span>}
    </label>
  );
}

function SocialSignupButton({ onClick }) {
  return (
    <button type="button" className="secondary-button social-signup-button" onClick={onClick}>
      <span className="google-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path fill="#4285F4" d="M22.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.9c-.3 1.3-1 2.4-2.1 3.1v2.6h3.4c2-1.8 3.4-4.5 3.4-7.5z" />
          <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.4-2.6c-.9.6-2.2 1-3.9 1-3 0-5.5-2-6.4-4.8H2.1v2.7C3.9 20.4 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.6 13.9c-.2-.6-.4-1.2-.4-1.9s.1-1.3.4-1.9V7.4H2.1C1.4 8.8 1 10.4 1 12s.4 3.2 1.1 4.6l3.5-2.7z" />
          <path fill="#EA4335" d="M12 5.3c1.6 0 3.1.6 4.2 1.7l3.1-3.1C17.5 2.1 15 1 12 1 7.7 1 3.9 3.6 2.1 7.4l3.5 2.7C6.5 7.3 9 5.3 12 5.3z" />
        </svg>
      </span>
      Continue with Google
    </button>
  );
}

function AnimatedMarketingScene({ state, relevantCard, form, validationField, passwordVisible, mode }) {
  const hasName = form.name.trim().length > 0;
  const hasEmail = form.email.trim().length > 0;
  const hasCompany = form.company.trim().length > 0;
  const isSubmitting = state === 'submitting';
  const isSuccess = state === 'success';
  const isError = state === 'error' || state === 'validationError';

  return (
    <section className="auth-visual signup-scene-panel" aria-hidden="true">
      <div className="auth-visual-copy signup-scene-copy">
        <p className="eyebrow">AI campaign engine</p>
        <h2>Marketing automation that reacts in real time.</h2>
        <p>Audience, email, campaign and analytics cards wake up as the form is completed.</p>
      </div>

      <div className="marketing-scene" data-mode={mode}>
        <div className="scene-ambient scene-ambient-one" />
        <div className="scene-ambient scene-ambient-two" />
        <div className="scene-grid" />

        <AIOrb state={state} passwordVisible={passwordVisible} />

        <MarketingCard
          className="campaign-card"
          icon={Megaphone}
          title="Campaign"
          badge={isSubmitting ? 'Optimizing…' : isSuccess ? 'Live' : hasCompany ? 'Campaign setup' : 'Ready'}
          active={relevantCard === 'campaign' || relevantCard === 'all' || relevantCard === 'success'}
          warning={relevantCard === 'campaign' && isError}
        >
          <div className="campaign-placeholder">
            <span className="campaign-title-line">{hasCompany ? `${form.company.trim()} launch` : 'Growth campaign'}</span>
            <span className="status-pill optimized">{isSuccess ? 'Workspace ready' : hasCompany ? 'Setup in progress' : 'Ready'}</span>
          </div>
          <div className="mini-progress" style={{ '--progress': hasCompany ? '60%' : isSubmitting ? '74%' : '24%' }}>
            <span />
          </div>
        </MarketingCard>

        <MarketingCard
          className="audience-card"
          icon={Target}
          title="Audience"
          badge={state === 'nameFocus' || state === 'nameTyping' ? 'New lead detected' : isSubmitting ? 'Analyzing…' : '+18% growth'}
          active={relevantCard === 'audience' || relevantCard === 'all' || relevantCard === 'success'}
          warning={relevantCard === 'audience' && isError}
        >
          <div className="audience-list">
            <span><i /> ICP: SaaS teams</span>
            <span><i /> Segment: High intent</span>
            {hasName && <span className="new-user-row"><i /> Lead: {form.name.trim().split(' ')[0]}</span>}
          </div>
        </MarketingCard>

        <MarketingCard
          className="email-card"
          icon={Mail}
          title="Email automation"
          badge={state === 'emailFocus' || state === 'emailTyping' ? 'Email ready' : isSubmitting ? 'Automating…' : '3-step flow'}
          active={relevantCard === 'email' || relevantCard === 'all' || relevantCard === 'success'}
          warning={relevantCard === 'email' && isError}
        >
          <div className="email-lines">
            <span />
            <span />
            <span />
          </div>
          <div className={`automation-path ${hasEmail || state === 'emailTyping' ? 'active' : ''}`}>
            <i /> <Send size={11} /> <i />
          </div>
        </MarketingCard>

        <MarketingCard
          className="analytics-card"
          icon={BarChart3}
          title="Analytics"
          badge={isSubmitting ? 'Rising' : isSuccess ? '+38% CVR' : 'AI Score 94'}
          active={relevantCard === 'all' || relevantCard === 'success'}
          warning={relevantCard === 'error'}
        >
          <div className="mini-bars">
            <span style={{ '--bar': '42%' }} />
            <span style={{ '--bar': '68%' }} />
            <span style={{ '--bar': isSubmitting || isSuccess ? '92%' : '56%' }} />
            <span style={{ '--bar': isSubmitting || isSuccess ? '78%' : '48%' }} />
          </div>
        </MarketingCard>

        <MarketingCard
          className="copy-card"
          icon={Wand2}
          title="Ad copy"
          badge="Generated"
          active={relevantCard === 'all' || relevantCard === 'success'}
          compact
        >
          <div className="post-card-preview">
            <span className="post-avatar" />
            <span className="post-line wide" />
            <span className="post-line" />
          </div>
        </MarketingCard>

        <MarketingCard
          className="conversion-card"
          icon={TrendingUp}
          title="Conversions"
          badge={isSuccess ? 'Workspace ready' : '+24.8%'}
          active={relevantCard === 'all' || relevantCard === 'success'}
          compact
        >
          <svg className="mini-line-chart" viewBox="0 0 120 42" aria-hidden="true">
            <path d="M4 34 C 22 30, 24 18, 42 22 S 70 30, 84 16 S 104 8, 116 10" />
          </svg>
        </MarketingCard>

        <div className="scene-badge ai-score-badge">AI Score <strong>94</strong></div>
        <div className="scene-badge conversion-badge"><CheckCircle2 size={12} /> {isSuccess ? 'Workspace ready' : 'CVR +24.8%'}</div>
        <div className={`scene-badge lead-badge ${state === 'nameFocus' || state === 'nameTyping' ? 'visible' : ''}`}>New lead detected</div>
        <div className={`scene-badge warning-badge ${isError ? 'visible' : ''}`}>
          <AlertTriangle size={12} /> {validationField ? 'Check field' : 'Try again'}
        </div>

        <FloatingIcon className="icon-megaphone" icon={Megaphone} />
        <FloatingIcon className="icon-cursor" icon={MousePointerClick} />
        <FloatingIcon className="icon-funnel" icon={Filter} />
        <FloatingIcon className="icon-mail" icon={Mail} />
        <FloatingIcon className="icon-spark-one" icon={Sparkles} />
        <FloatingIcon className="icon-spark-two" icon={Sparkles} />
      </div>
    </section>
  );
}

function AIOrb({ state, passwordVisible }) {
  const secureState = state === 'passwordFocus' || state === 'passwordVisible';

  return (
    <div className={`ai-orb-shell ${secureState ? 'secure' : ''}`}>
      <div className="ai-orb-ripple" />
      <div className="ai-orb">
        <span className="orb-ring orb-ring-one" />
        <span className="orb-ring orb-ring-two" />
        <span className="orb-core">
          <BrainCircuit size={34} />
        </span>
        <span className="orb-node node-one" />
        <span className="orb-node node-two" />
        <span className="orb-node node-three" />
      </div>
      <div className={`orb-security ${secureState ? 'visible' : ''} ${passwordVisible ? 'open' : ''}`}>
        {passwordVisible ? <Eye size={13} /> : <LockKeyhole size={13} />}
      </div>
    </div>
  );
}

function MarketingCard({ className, icon: Icon, title, badge, active, warning, compact, children }) {
  return (
    <div className={`mkt-card-shell ${className} ${active ? 'active' : ''} ${warning ? 'warning' : ''} ${compact ? 'compact' : ''}`}>
      <div className="mkt-card">
        <div className="mkt-card-head">
          <span className="mkt-card-icon"><Icon size={15} /></span>
          <strong>{title}</strong>
          <span className="mkt-card-badge">{badge}</span>
        </div>
        <div className="mkt-card-body">
          {children}
        </div>
      </div>
    </div>
  );
}

function FloatingIcon({ className, icon: Icon }) {
  return (
    <span className={`floating-marketing-icon ${className}`}>
      <Icon size={15} />
    </span>
  );
}

export default AuthModal;
